import { MappingContext } from '@app/mapping/MappingContext';
import { JSX, FC, useCallback, useContext, useRef, useState, useEffect } from 'react';
import * as ReactMapGL from "react-map-gl/mapbox";
import { Source, Layer, NavigationControl, type MapRef } from "react-map-gl/mapbox";
import LoadingSpinner from '@app/shared/LoadingSpinner';
import { MapboxSupportAlert } from '@app/shared/mapping/MapboxSupportAlert';
import { RecenterControl } from '@app/shared/mapping/RecenterControl';
import { MarketExpansionMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext";
import { MembershipMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MembershipMapDataContext";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { MembershipColorTheme } from "@app/overviews/market-expansion-dashboard/contexts/MembershipColorThemeContext";
import GradientLegend from "@app/overviews/market-expansion-dashboard/components/GradientLegend";
import { MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionLineOfBusinessConfig";
import { MapAppearanceControl } from "@app/shared/map-appearance/components/MapAppearanceControl";
import {
  COUNTY_OUTLINE_COLORS,
  COUNTY_SHADING_BASE,
  EMPTY_FEATURE_COLLECTION,
  HOVERED_COUNTY_BORDER_LAYER_ID,
  HOVERED_COUNTY_SOURCE_ID,
  MAP_DEFAULT_ZOOM,
  SELECTED_COUNTY_BORDER_LAYER_ID,
  SELECTED_COUNTY_SOURCE_ID,
  MEMBERSHIP_SHADED_COUNTY_BORDER_LAYER_ID,
  MEMBERSHIP_SHADED_COUNTY_FILL_LAYER_ID,
  MEMBERSHIP_SHADED_COUNTY_SOURCE_ID,
} from '@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants';
import MarketOpportunityCountySummary from '@app/overviews/market-expansion-dashboard/components/membership-map/MarketOpportunityCountySummary';

const WrappedMembershipMap: FC = (): JSX.Element => {
  const mappingContext = useContext(MappingContext);
  const mapDataContext = useContext(MarketExpansionMapDataContext);
  const membershipMapDataContext = useContext(MembershipMapDataContext);
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const mapRef = useRef<MapRef>(null);

  const { getColor } = MembershipColorTheme.useMapColorTheme();
  const minMetricColor = getColor("minMetricColor");
  const maxMetricColor = getColor("maxMetricColor");

  const imagesDir = `${window.customUIBaseUrl}/images/`;

  const [viewState, setViewState] = useState<ReactMapGL.ViewState>({ ...mappingContext.defaultViewState, zoom: MAP_DEFAULT_ZOOM });

  const onMapLoad = useCallback((): void => {
    const map = mapRef.current?.getMap();

    if (!map) return;

    mapRef.current?.setCenter([mappingContext.defaultLongitude, mappingContext.defaultLatitude]);

    mapRef.current?.on('mousemove', MEMBERSHIP_SHADED_COUNTY_FILL_LAYER_ID, (e) => mapDataContext.handleMouseMoveOnCounty(e));
    mapRef.current?.on('mouseleave', MEMBERSHIP_SHADED_COUNTY_FILL_LAYER_ID, () => mapDataContext.handleMouseLeaveOnCounty());
    mapRef.current?.on('click', MEMBERSHIP_SHADED_COUNTY_FILL_LAYER_ID, (e) => mapDataContext.handleMouseClickOnCounty(e));
  }, []);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: mappingContext.defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: mappingContext.defaultPitch,
    });
  }, []);

  const recenterMap = useCallback((): void => {
    setViewState({ ...mappingContext.defaultViewState, zoom: MAP_DEFAULT_ZOOM });
    mapDataContext.setMinZoomLevel(mappingContext.defaultZoom - .5);
  }, []);

  const handleRecenterIconClick = () => {
    if (mapDataContext.dataLoaded && !!mapDataContext.dataBoundingBox) {
      mapRef.current?.fitBounds(mapDataContext.dataBoundingBox, mapDataContext.easingOptions());
    } else {
      recenterMap();
    }
  }

  const lineOfBusinessConfig = filtersContext.selectedLineOfBusiness
    ? MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG[filtersContext.selectedLineOfBusiness]
    : undefined;

  const legendTitle = lineOfBusinessConfig?.legendTitle ?? "";

  const metricValues = membershipMapDataContext.membershipShadedCountyData?.features.map(
    (f) => f.properties?.metricValue ?? 0
  ) ?? [];

  const maxMetricValue = metricValues.length ? Math.max(...metricValues) : 0;
  const minMetricValue = metricValues.length ? Math.min(...metricValues) : 0;
  // Every county resolved to a metric value of 0 (e.g. a state with no Medicaid programs on file) — show
  // outlines only, without a misleadingly flat shading fill or a "0 to 0" legend.
  const hasShadingData = maxMetricValue > 0;

  const rawMinShadingStop = Math.pow(minMetricValue, COUNTY_SHADING_BASE);
  const maxShadingStop = Math.pow(Math.max(maxMetricValue, 1), COUNTY_SHADING_BASE);
  const minShadingStop = rawMinShadingStop < maxShadingStop ? rawMinShadingStop : maxShadingStop - 1;

  useEffect(() => {
    if (membershipMapDataContext.membershipShadedCountyData) {
      handleRecenterIconClick();
    }
  }, [mapDataContext.dataBoundingBox, mapDataContext.dataLoaded, mapDataContext.selectedTooltipHeight, membershipMapDataContext.membershipShadedCountyData])

  useEffect(() => {
    if (!filtersContext.selectedState) {
      mapDataContext.resetMapData();
      recenterMap();
    }
  }, [filtersContext.selectedState])

  return (
    <MapboxSupportAlert>
      <div className="market-expansion-dashboard__map-container" data-testid="market-expansion-membership__map">
        <ReactMapGL.Map
          {...viewState}
          {...mappingContext.defaultMapProps}
          reuseMaps={true}
          onLoad={onMapLoad}
          onMove={(e) => onMapMove(e.viewState)}
          ref={mapRef}
        >
          {/* Layers are sorted from top-most to bottom-most and using beforeId to avoid mapbox console errors */}

          <Source id={HOVERED_COUNTY_SOURCE_ID} type="geojson" data={mapDataContext.hoverCountyBorderData ?? EMPTY_FEATURE_COLLECTION}>
            <Layer
              id={HOVERED_COUNTY_BORDER_LAYER_ID}
              type="line"
              paint={{
                "line-color": COUNTY_OUTLINE_COLORS.hovered,
                "line-offset": 0.5,
                "line-width": 4,
              }}
            />
          </Source>

          <Source id={SELECTED_COUNTY_SOURCE_ID} type="geojson" data={mapDataContext.selectedCountyBorderData ?? EMPTY_FEATURE_COLLECTION}>
            {membershipMapDataContext.membershipShadedCountyData && (
              <Layer
                id={SELECTED_COUNTY_BORDER_LAYER_ID}
                beforeId={HOVERED_COUNTY_BORDER_LAYER_ID}
                type="line"
                paint={{
                  "line-color": COUNTY_OUTLINE_COLORS.selected,
                  "line-width": 1.6,
                }}
              />
            )}
          </Source>

          <Source id={MEMBERSHIP_SHADED_COUNTY_SOURCE_ID} type="geojson" data={membershipMapDataContext.membershipShadedCountyData ?? EMPTY_FEATURE_COLLECTION}>
            {/* County outline layer. */}
            <Layer
              id={MEMBERSHIP_SHADED_COUNTY_BORDER_LAYER_ID}
              beforeId={SELECTED_COUNTY_BORDER_LAYER_ID}
              type="line"
              paint={{
                "line-color": "grey",
                "line-offset": 0,
                "line-width": 1,
              }}
            />

            {/* County fill layer. */}
            <Layer
              id={MEMBERSHIP_SHADED_COUNTY_FILL_LAYER_ID}
              beforeId={MEMBERSHIP_SHADED_COUNTY_BORDER_LAYER_ID}
              type="fill"
              paint={{
                "fill-color": [
                  "interpolate",
                  ["linear"],
                  ["^", ["coalesce", ["get", "metricValue"], 0], COUNTY_SHADING_BASE],
                  minShadingStop, minMetricColor,
                  maxShadingStop, maxMetricColor
                ],
                "fill-opacity": membershipMapDataContext.membershipShadedCountyData && hasShadingData ? 1 : 0
              }}
            />
          </Source>

          {membershipMapDataContext.membershipShadedCountyData && filtersContext.selectedState && (
            <>
              <MarketOpportunityCountySummary isCountySelectionPopup={false} />
              <MarketOpportunityCountySummary isCountySelectionPopup={true} />
            </>
          )}

          {membershipMapDataContext.membershipShadedCountyData && filtersContext.selectedState && hasShadingData && (
            <GradientLegend
              qesTestId={`market-expansion-dashboard__${filtersContext.selectedLineOfBusiness ?? ""}-market-opportunity-legend`}
              maxColor={maxMetricColor}
              maxValue={maxMetricValue}
              minColor={minMetricColor}
              legendTitle={legendTitle}
              minValue={lineOfBusinessConfig?.legendUsesMinValue ? minMetricValue : 0} />
          )}

          <MapAppearanceControl
            colorThemeContext={MembershipColorTheme.Context}
            colorThemeFlyoutTitle="Membership Map Color Theme"
            colorThemeSelectLabel="Color Theme"
            mapRef={mapRef}
            position="bottom-right"
            testId="market-expansion-membership"
          />

          <RecenterControl
            ignoreInMapExport={true}
            imagePath={`${imagesDir}Recenter.png`}
            mapRef={mapRef}
            onClick={handleRecenterIconClick}
            position="bottom-right"
          />

          <NavigationControl data-html2canvas-ignore="true" position="bottom-right" showCompass={false} />

        </ReactMapGL.Map>
      </div>
    </MapboxSupportAlert>
  );
}

export const MembershipMap: FC = (): JSX.Element => {
  const { errorLoadingSettings, loadingDefaultSettings } = useContext(MappingContext);
  if (loadingDefaultSettings) {
    return (<div className="market-expansion-dashboard__map-container" data-testid="market-expansion__membership-map__loading">
      <LoadingSpinner />
    </div>);
  } else if (errorLoadingSettings) {
    return (<div className="market-expansion-dashboard__map-container" role="alert">An error has occurred</div>);
  };

  return (
    <MembershipColorTheme.Provider>
      <WrappedMembershipMap />
    </MembershipColorTheme.Provider>
  );
};